import { ProgressIndicator, Wizard } from '@piggybank/form';
import { StyleProvider } from '@piggybank/global-toolkit';
import get from 'lodash/get';
import keys from 'lodash/keys';
import pickBy from 'lodash/pickBy';
import template from 'lodash/template';
import T from 'prop-types';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import {
  FormContextProvider,
  Locale,
  Translation,
  TranslationScope,
} from './context';
import { pruneUnwantedProperties, requiredArg } from './utils';
import { compileSchema } from './validation';
import { createRenderer } from './';

export const DynamicForm = forwardRef(
  (
    {
      formSchema = requiredArg('formSchema'),
      formName = 'dynamic-form',
      onSubmit = requiredArg('onSubmit'),
      translations = requiredArg('translations'),
      culture = requiredArg('culture'),
      customComponents = {},
      customPageComponents = {},
      customUIComponents = {},
      initialCurrentPage = 0,
      initialValues = {},
      pagesConfig = {},
      productCodes = {},
      readOnlyReviewSections = [],
      showProgressIndicator = true,
      showNavigation = true,
      extendAjv = () => null,
    },
    ref
  ) => {
    const [localeKey, setLocaleKey] = useState(new Date().getTime());

    // DF REPL needs a key prop to force update locale translation
    useEffect(() => {
      if (global.__DEV__) {
        setLocaleKey(new Date().getTime());
      }
    }, [JSON.stringify({ culture, productCodes, translations })]);

    const validate = compileSchema(formSchema, { productCodes }, extendAjv);

    const excludePages = useMemo(
      () => keys(pickBy(pagesConfig, value => value.exclude === true)),
      []
    );

    let submitForm;

    const bindSubmitForm = submitFormikForm => {
      if (!submitForm) {
        submitForm = submitFormikForm;
      }
    };

    useImperativeHandle(ref, () => ({
      submitForm,
    }));

    const { getFormStructure, renderChildren, setFormStructure } =
      createRenderer({
        validate,
        customComponents,
        customPageComponents,
        customUIComponents,
        excludePages,
        bindSubmitForm,
        showNavigation,
      });

    const getValidatedFormData = values => {
      // AJV coerces the form data types ahead of sending to bpAPI
      // e.g. string -> number if specificed number in schema
      validate(values);

      return values;
    };

    const lastPageIndex = useMemo(
      () =>
        keys(get(validate, 'schema.properties', {})).filter(
          pageKey => !excludePages.includes(pageKey)
        ).length - 1,
      []
    );

    // initial formStructure based on prepopulated formValues
    setFormStructure(initialValues);

    return (
      <FormContextProvider
        customComponents={customComponents}
        pagesConfig={pagesConfig}
        review={{ readOnlyReviewSections }}
      >
        <Locale
          culture={culture}
          key={localeKey}
          productCodes={productCodes}
          translations={translations}
        >
          <TranslationScope path="global">
            <StyleProvider>
              <Wizard
                initialCurrentPage={initialCurrentPage}
                initialValues={initialValues}
                onSubmit={values =>
                  onSubmit(
                    getValidatedFormData(
                      pruneUnwantedProperties(values, getFormStructure())
                    )
                  )
                }
              >
                {showProgressIndicator && (
                  <Translation
                    fallbackValue={{
                      stage: {
                        application: 'Application',
                      },
                      progressTemplate: 'Step ${ current } of ${ total }',
                    }}
                    path="stepTracker"
                  >
                    {({ progressTemplate, stage }) => (
                      <ProgressIndicator
                        renderStepText={progressProps => {
                          const progress =
                            template(progressTemplate)(progressProps);

                          return (
                            <>
                              <strong>{stage.application}</strong> - {progress}
                            </>
                          );
                        }}
                      />
                    )}
                  </Translation>
                )}
                {renderChildren({
                  context: {
                    parent: 'DynamicForm',
                    keyPath: {},
                    schemaPath: [],
                    formName,
                    lastPageIndex,
                    translations,
                  },
                  pagesConfig,
                })}
              </Wizard>
            </StyleProvider>
          </TranslationScope>
        </Locale>
      </FormContextProvider>
    );
  }
);

DynamicForm.displayName = 'DynamicForm';

DynamicForm.propTypes = {
  formSchema: T.object.isRequired,
  formName: T.string,
  translations: T.object.isRequired,
  culture: T.object.isRequired,
  onSubmit: T.func.isRequired,
  customComponents: T.object,
  customPageComponents: T.object,
  customUIComponents: T.object,
  readOnlyReviewSections: T.arrayOf(T.string),
  showProgressIndicator: T.bool,
  showNavigation: T.bool,
  initialCurrentPage: T.number,
  initialValues: T.object,
  pagesConfig: T.object,
  productCodes: T.object,
  extendAjv: T.func,
  excludePages: T.arrayOf(T.string),
};
